import { useContext, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';
import { BasicCard } from '../../components/Card';
import { Header } from '../../components/Header';
import { LoadingScreen } from '../../components/LoadingScreen';
import { Button } from '../../components/Button';
import { UpdateDataModal } from '../../components/UpdateDataModal';
import { Contract } from '../../types/ContractType';
import { UserDataContext } from '../../providers/userData';
import { TextComponent } from '../../components/TextComponent';
import { Container, GreetingContainer, ItemsContainer, RoleContainer } from './styles';
import { useTheme } from '../../providers/theme';
import { useTranslation } from 'react-i18next';
import { DialogAlert } from '../../components/DialogAlert';

type ContractListProps = {
  isLoading: boolean;
  contractsData: Contract[];
  onEditContractData: Contract;
  isEditContractModalOpen: boolean;
  setIsEditContractModalOpen: (value: boolean) => void;
  getContractsData: () => void;
  onEdit: (contract: Contract) => void;
  onDelete: (contractId: string) => void;
}

export function ContractList(
  {
    isLoading, contractsData, onEditContractData,
    isEditContractModalOpen, setIsEditContractModalOpen, getContractsData, onEdit, onDelete
  }: ContractListProps) {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string>('');
  const [contractNumber, setContractNumber] = useState<string>('');

  useEffect(() => {
    getContractsData();
  }, []);

  function handleDeleteContract(contractId: string, contractNumber: string) {
    setDialogOpen(true);
    setContractId(contractId);
    setContractNumber(contractNumber);
  }

  function handleDeleteContractConfirmed() {
    setDialogOpen(false);
    if(contractId){
      onDelete(contractId);
    }
  }

  function handleClose() {
    setDialogOpen(false);
  }

  return (
    <>
      {isLoading ? (
        <LoadingScreen isVisible={isLoading} />
      ) : (
        <>
          <Header />

          <Container>
            <GreetingContainer>
              <TextComponent
                color='#000'
              >
                {t('group-welcome')}
              </TextComponent>
              <TextComponent
                children={`${userData.userName} 🚀`}
                fontWeight='bold'
                color='#000'
              />
            </GreetingContainer>

            <RoleContainer>
              <TextComponent
                color='#000'
              >
                {t('group-intro')}
              </TextComponent>

              {userData.admin === true ? (
                <TextComponent
                  fontWeight='bold'
                  color='#000'
                >
                  {t('group-admin')}
                </TextComponent>
              ) : (
                <TextComponent
                  fontWeight='bold'
                  color='#000'
                >
                  {t('group-user')}
                </TextComponent>
              )}
            </RoleContainer>

            <ItemsContainer>
              <TextComponent
                fontWeight='bold'
              >
                {t('group-header')}
              </TextComponent>

              {userData.admin && (
                <Button
                  id='newContract'
                  onClick={() => navigate('/contract')}
                  type='button'
                  color={theme.colors.main}
                  sxhover={theme.colors.hoverMain}
                  sxwidth='175px'
                  sxgap='10px'
                >
                  <Plus size={20} weight='bold' />
                  <TextComponent
                    fontWeight='bold'
                    fontSize='14px'
                    color='#FFF'
                  >
                    {t('group-button')}
                  </TextComponent>
                </Button>
              )}
            </ItemsContainer>

            <Grid container style={{ marginRight: 100, display: 'flex', flexDirection: 'row', gap: 10 }}>
              {contractsData?.map(contract => (
                <div key={contract.id}>
                  <BasicCard
                    contractData={contract}
                    number={contract.number}
                    ownerId={contract.owner_id}
                    onEdit={onEdit}
                    onDelete={() => handleDeleteContract(contract.id, contract.number)}
                  />
                </div>
              ))}
            </Grid>
          </Container>
          {isEditContractModalOpen && (
            <UpdateDataModal
              onEditProject={false}
              onEditContract={true}
              dataContract={onEditContractData}
              isOpen={isEditContractModalOpen}
              toggle={() => setIsEditContractModalOpen(false)}
              getContractsData={getContractsData}
              getProjectsData={() => null}
            />
          )}
        </>
      )}
      <DialogAlert
        handleDelete={handleDeleteContractConfirmed}
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        dialogTitle={t('delete-option-contract')}
        dialogMessage={t('delete-verification-contract', {contractNumber})}
      />
    </>
  );
}
